import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { LoadingScreen } from 'components/pages/requests/empty-screens'
import { Project } from 'interfaces/project'
import { Ticket } from 'interfaces/ticket'
import { requestQuery } from 'lib/api/fetch-api'
import { Drawer } from 'lib/components/drawer/drawer'
import { updateParams } from 'lib/util/url'
import { useState } from 'react'
import AddDeliverablesContent from './add-deliverables-content'
import CreateDeliverablesContent from './create-deliverables-content'
import ProjectDetailsContent from './project-details-content'
import ProjectReadOnlyContent from './project-read-only-content'
import RemoveDeliverablesContent from './remove-deliverables-content'

export type DrawerState = 'details' | 'add-deliverables' | 'remove-deliverables' | 'read-only' | 'create-deliverables'

export enum ProjectUpdateType {
  UPDATE = 'update',
  DELETE = 'delete',
}

export interface ProjectDrawerProps {
  isOpen: boolean
  setOpen: (open: boolean) => void
  projectId?: number
  onUpdate?: (project: Project, action: ProjectUpdateType) => void
  project?: Project
  readOnly?: boolean
}

export default function ProjectDrawer({
  isOpen,
  setOpen,
  projectId,
  onUpdate,
  project: passedProject,
  readOnly,
}: ProjectDrawerProps) {
  const params = new URLSearchParams(window.location.search)
  const [drawerState, setDrawerState] = useState<DrawerState>(
    readOnly ? 'read-only' : (params.get('projectTab') as DrawerState) || 'details',
  )
  const [deliverablesToRemove, setDeliverablesToRemove] = useState<Ticket[]>([])

  const { data, refetch, isFetching } = useQuery({
    queryKey: ['project', projectId],
    queryFn: requestQuery({ endpoint: 'getProject', query: { id: projectId } }),
    enabled: isOpen && !readOnly,
    placeholderData: keepPreviousData,
  })

  const fetchedProject = data?.data
  const project = readOnly ? passedProject || fetchedProject : fetchedProject

  const navigateTab = (tab: DrawerState) => {
    setDrawerState(tab)
    params.set('projectTab', tab)
    updateParams(params)
  }

  const handleRemoveDeliverablesClick = (deliverablesToRemove: Ticket[]) => {
    setDeliverablesToRemove(deliverablesToRemove)
    navigateTab('remove-deliverables')
  }

  const onUpdateProject: (updatedProject?: Project, action?: ProjectUpdateType) => void = async (
    updatedProject,
    action: ProjectUpdateType = ProjectUpdateType.UPDATE,
  ) => {
    if (action === ProjectUpdateType.UPDATE) {
      await refetch()
    }

    onUpdate?.(updatedProject, action)
  }

  const updateOpen = (open: boolean) => {
    setOpen(open)
    if (!open) {
      params.delete('projectTab')
      params.delete('projectId')
      params.delete('duplicate')
      updateParams(params)
    }
  }

  if (isFetching) {
    return (
      <Drawer isOpen={isOpen} setOpen={updateOpen} size="md">
        <LoadingScreen />
      </Drawer>
    )
  }

  return (
    <Drawer isOpen={isOpen} setOpen={updateOpen} size="md">
      {drawerState === 'read-only' && (
        <ProjectReadOnlyContent project={project} setOpen={updateOpen} isLoading={isFetching} />
      )}
      {drawerState === 'details' && (
        <ProjectDetailsContent
          isOpen={isOpen}
          setOpen={updateOpen}
          project={project}
          isLoading={isFetching}
          setDrawerState={navigateTab}
          onRemoveDeliverablesClick={handleRemoveDeliverablesClick}
          onUpdate={onUpdateProject}
        />
      )}
      {drawerState === 'add-deliverables' && (
        <AddDeliverablesContent
          project={project}
          setDrawerState={navigateTab}
          setOpen={updateOpen}
          onUpdate={onUpdateProject}
        />
      )}
      {drawerState === 'remove-deliverables' && (
        <RemoveDeliverablesContent
          project={project}
          setDrawerState={navigateTab}
          setOpen={updateOpen}
          deliverablesToRemove={deliverablesToRemove}
          setDeliverablesToRemove={setDeliverablesToRemove}
          onUpdate={onUpdateProject}
        />
      )}
      {drawerState === 'create-deliverables' && (
        <CreateDeliverablesContent
          project={project}
          setDrawerState={navigateTab}
          setOpen={updateOpen}
          onUpdate={onUpdateProject}
        />
      )}
    </Drawer>
  )
}

export const Divider = () => <span className="tw-h-5 tw-w-[1px] tw-bg-neutral-500" />
